import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { isAxiosError } from 'axios'
import { useDebounce } from '@app/hooks/useDebounce'
import { appLink } from '@app/config/appLink'
import { toast } from '@app/components/ui/Toast/toast'
import { DocumentsToolbar } from '../components/documents-toolbar'
import { DocumentsTable } from '../components/documents-table'
import { DocumentsPagination } from '../components/documents-pagination'
import { DocumentsFiltersSheet } from '../components/documents-filters-sheet'
import { DocumentPreviewDialog } from '../components/document-preview-dialog'
import { DocumentDeleteDialog } from '../components/document-delete-dialog'
import { UploadWizardDialog } from '../components/upload-wizard/upload-wizard-dialog'
import { EditWizardDialog } from '../components/upload-wizard/edit-wizard-dialog'
import { useDeleteDocument, useFetchDocument, useFetchDocuments } from '../hooks/useDocuments'
import { useDocumentsUrlState } from '../hooks/useDocumentsUrlState'
import { DEFAULT_VISIBLE_COLUMNS } from '../documents.constants'
import { DocumentColumnKey, DocumentsFilters, EvidenceDocument } from '../documents.types'

export const DocumentsLibraryPage = () => {
  const { id: routeDocumentId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const { query, setQuery } = useDocumentsUrlState()
  // Local/immediate value for responsiveness while typing; pushed to the URL only once
  // debounced, so every keystroke doesn't spam history entries.
  const [searchInput, setSearchInput] = useState(query.search)
  const [visibleColumns, setVisibleColumns] = useState<DocumentColumnKey[]>(DEFAULT_VISIBLE_COLUMNS)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<EvidenceDocument | null>(null)
  const [uploadWizardOpen, setUploadWizardOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<EvidenceDocument | null>(null)

  const debouncedSearch = useDebounce(searchInput, 300)

  useEffect(() => {
    if (debouncedSearch !== query.search) setQuery({ search: debouncedSearch, page: 1 })
    // Only the debounced value settling should push a URL update — query/setQuery aren't
    // meant to re-trigger this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  // Keeps the search box in sync when the URL changes elsewhere (browser back/forward) —
  // safe against looping since the effect above only fires when debouncedSearch actually
  // differs from query.search.
  useEffect(() => {
    setSearchInput(query.search)
  }, [query.search])

  const { data, isLoading, isFetching } = useFetchDocuments(query)
  const deleteDocument = useDeleteDocument()

  // /app/documents/:id — a "Share as a link" URL, or the table's own Title link. Fetched
  // independently of the (paginated, filtered) list above, since the target document may not be
  // on the currently loaded page or may not match the current filters at all.
  const routeDocumentQuery = useFetchDocument(routeDocumentId, !!routeDocumentId)

  useEffect(() => {
    if (!routeDocumentQuery.isError) return
    const status = isAxiosError(routeDocumentQuery.error)
      ? routeDocumentQuery.error.response?.status
      : undefined
    toast({
      description:
        status === 403
          ? "You don't have access to this document."
          : 'That document could not be found.',
      position: 'top-right',
      type: 'error',
    })
    navigate({ pathname: appLink.documents.path, search: location.search }, { replace: true })
    // Fires once per failed fetch, not on every render — navigate/location.search are stable
    // enough here that including them would just risk re-running this on unrelated navigations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeDocumentQuery.isError, routeDocumentQuery.error])

  const updateFilters = (next: Partial<DocumentsFilters>) => {
    setQuery({ ...next, page: 1 })
  }

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return
    deleteDocument.mutate(deleteTarget.id)
    setDeleteTarget(null)
  }

  const closePreview = () => {
    if (routeDocumentId) {
      navigate({ pathname: appLink.documents.path, search: location.search }, { replace: true })
    }
  }

  return (
    <div className="h-full overflow-y-auto flex items-center flex-col">
      <div className="p-8 max-w-[1400px] w-full flex flex-col gap-4">
        <div>
          <h1 className="text-3xl font-semibold mb-2 text-rexBlue-900">Documents</h1>
          <p className="w-2/3 text-neutral-600">
            Browse, search, and upload your risk and resilience documents.
          </p>
        </div>

        <div className="flex items-center justify-between">
          <span className="font-medium">
            All documents <span className="text-neutral-400">{data?.total_count ?? 0}</span>
          </span>
        </div>

        <DocumentsToolbar
          search={searchInput}
          onSearchChange={setSearchInput}
          category={query.category}
          onCategoryChange={(category) => updateFilters({ category })}
          tags={data?.tags ?? []}
          selectedTags={query.tags}
          onTagsChange={(tags) => updateFilters({ tags })}
          tagsLoading={isLoading || isFetching}
          visibleColumns={visibleColumns}
          onVisibleColumnsChange={setVisibleColumns}
          onOpenFilters={() => setFiltersOpen(true)}
          onUpload={() => setUploadWizardOpen(true)}
        />

        <DocumentsTable
          documents={data?.documents ?? []}
          visibleColumns={visibleColumns}
          sort={query.sort}
          onSortChange={(next) => setQuery({ sort: next, page: 1 })}
          search={location.search}
          onEdit={setEditTarget}
          onDelete={setDeleteTarget}
          isLoading={isLoading || isFetching}
        />

        {data && data.total_count > 0 && (
          <DocumentsPagination
            page={query.page}
            pageSize={query.pageSize}
            pageCount={data.page_count}
            totalCount={data.total_count}
            rowsShown={data.documents.length}
            onPageChange={(page) => setQuery({ page })}
            onPageSizeChange={(pageSize) => setQuery({ pageSize, page: 1 })}
          />
        )}
      </div>

      <DocumentsFiltersSheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        filters={query}
        onApply={(next) => updateFilters(next)}
      />

      <DocumentPreviewDialog
        document={routeDocumentQuery.data ?? null}
        open={!!routeDocumentId}
        onOpenChange={(open) => !open && closePreview()}
      />

      <DocumentDeleteDialog
        document={deleteTarget}
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        isDeleting={deleteDocument.isPending}
      />

      <UploadWizardDialog open={uploadWizardOpen} onOpenChange={setUploadWizardOpen} />

      <EditWizardDialog
        document={editTarget}
        open={editTarget !== null}
        onOpenChange={(open) => !open && setEditTarget(null)}
      />
    </div>
  )
}
